import React from 'react';
import { FileText, User, DollarSign, Printer } from 'lucide-react';
import { ScoreRing } from './ui/ScoreRing';
const scores = {
  sentiment: 8,
  deal: 9,
  callReview: 8
};
const overallScore = Math.round((scores.sentiment + scores.deal + scores.callReview) / 3 * 10) / 10;
interface SidebarProps {
  onNavigate?: (section: string) => void;
  activeSection?: string | null;
  onPrint?: () => void;
}
export function Sidebar({
  onNavigate,
  activeSection,
  onPrint
}: SidebarProps) {
  const scoreItems = [{
    key: 'sentiment',
    label: 'Sentiment',
    score: scores.sentiment
  }, {
    key: 'deal',
    label: 'Deal Possibilities',
    score: scores.deal
  }, {
    key: 'callReview',
    label: 'Call Review',
    score: scores.callReview
  }];
  return <div className="w-full lg:w-[280px] lg:fixed lg:inset-y-0 lg:left-0 bg-navy text-white flex flex-col z-10 border-t-4 border-crimson" data-id="element-334">
      {/* Mobile Header (Sticky) */}
      <div className="lg:hidden flex items-center justify-between p-4 border-b border-navy-light bg-navy sticky top-0 z-20" data-id="element-335">
        <div className="flex items-center gap-2" data-id="element-336">
          <span className="text-crimson font-serif text-xl tracking-tight font-bold" data-id="element-337">
            NMHL
          </span>
          <span className="text-white font-sans font-medium text-sm" data-id="element-338">
            Paul Rutherford
          </span>
        </div>
        <div className="flex gap-3 items-center" data-id="element-339">
          <div className="flex flex-col items-center" data-id="element-340">
            <span className="text-xs text-gray-300 uppercase font-sans" data-id="element-341">
              Overall
            </span>
            <span className="text-white font-sans font-bold text-lg" data-id="element-342">
              {overallScore}
            </span>
          </div>
          <div className="w-px h-6 bg-navy-light" data-id="element-343" />
          {scoreItems.map(item => <button key={item.key} onClick={() => onNavigate?.(item.key)} className="flex flex-col items-center cursor-pointer hover:opacity-80 transition-opacity" data-id="element-344">
              <span className="text-xs text-gray-300 uppercase font-sans" data-id="element-345">
                {item.label.slice(0, 4)}
              </span>
              <span className={`font-sans font-bold ${activeSection === item.key ? 'text-white' : 'text-crimson'}`} data-id="element-346">
                {item.score}
              </span>
            </button>)}
        </div>
      </div>

      {/* Desktop Sidebar Content */}
      <div className="hidden lg:flex flex-col h-full overflow-y-auto" data-id="element-347">
        {/* Brand */}
        <div className="p-6 border-b border-navy-light" data-id="element-348">
          <div className="flex items-center gap-3 mb-2" data-id="element-349">
            <span className="text-crimson font-serif text-3xl tracking-tight font-bold" data-id="element-350">
              NMHL
            </span>
          </div>
          <p className="text-xs text-gray-300 uppercase tracking-wider leading-tight font-sans" data-id="element-351">
            National Mortgage
            <br data-id="element-352" />
            Home Loans
          </p>
        </div>

        {/* Borrower Info */}
        <div className="p-6 border-b border-navy-light space-y-6" data-id="element-353">
          <div data-id="element-354">
            <p className="text-crimson text-xs font-semibold uppercase tracking-widest mb-1 font-sans" data-id="element-355">
              Internal Report
            </p>
            <h1 className="text-xl text-white font-serif" data-id="element-356">Call Analysis</h1>
          </div>

          <div className="space-y-4" data-id="element-357">
            <div className="flex flex-col gap-1" data-id="element-358">
              <span className="text-gray-300 uppercase tracking-wider text-xs flex items-center gap-1.5 font-sans" data-id="element-359">
                <User size={13} data-id="element-360" /> Borrower
              </span>
              <span className="font-medium text-white text-sm font-sans" data-id="element-361">
                Paul Rutherford
              </span>
            </div>
            <div className="flex flex-col gap-1" data-id="element-362">
              <span className="text-gray-300 uppercase tracking-wider text-xs flex items-center gap-1.5 font-sans" data-id="element-363">
                <FileText size={13} data-id="element-364" /> Lead Status
              </span>
              <span className="font-medium text-white text-sm font-sans" data-id="element-365">
                Demo Requested
              </span>
            </div>
            <div className="flex flex-col gap-1" data-id="element-366">
              <span className="text-gray-300 uppercase tracking-wider text-xs flex items-center gap-1.5 font-sans" data-id="element-367">
                <DollarSign size={13} data-id="element-368" /> Loan Amount
              </span>
              <span className="font-medium text-white text-sm font-sans" data-id="element-369">
                $N/A
              </span>
            </div>
          </div>
        </div>

        {/* Key Scores */}
        <div className="p-6 flex-1" data-id="element-370">
          <h3 className="text-xs font-semibold text-gray-300 uppercase tracking-wider mb-6 font-sans" data-id="element-371">
            Key Scores
          </h3>

          {/* Overall Score - Prominent */}
          <div className="flex flex-col items-center mb-8 pb-6 border-b border-navy-light" data-id="element-372">
            <span className="text-xs font-semibold text-gray-300 uppercase tracking-widest mb-3 font-sans" data-id="element-373">
              Overall
            </span>
            <ScoreRing score={overallScore} dynamicColor data-id="element-374" />
          </div>

          <div className="space-y-2" data-id="element-375">
            {scoreItems.map(item => <button key={item.key} onClick={() => onNavigate?.(item.key)} className={`w-full flex items-center justify-between px-3 py-3 rounded-lg cursor-pointer transition-all duration-200 ${activeSection === item.key ? 'bg-crimson/15 ring-1 ring-crimson/30' : 'hover:bg-white/5'}`} data-id="element-376">
                <span className={`text-sm font-sans font-medium transition-colors duration-200 ${activeSection === item.key ? 'text-white' : 'text-gray-200'}`} data-id="element-377">
                  {item.label}
                </span>
                <ScoreRing score={item.score} size="sm" dynamicColor data-id="element-378" />
              </button>)}
          </div>
        </div>

        {/* Print + Keyboard Hint */}
        <div className="p-4 border-t border-navy-light space-y-3" data-id="element-379">
          <button onClick={onPrint} className="w-full flex items-center justify-center gap-2 text-xs font-medium bg-white/10 hover:bg-white/20 px-3 py-2 rounded transition-colors text-white" data-id="element-380">
            <Printer size={14} data-id="element-381" />
            <span data-id="element-382">Print Report</span>
          </button>
          <p className="text-xs text-gray-400 font-sans flex items-center justify-center gap-2" data-id="element-383">
            Press{' '}
            <kbd className="px-1.5 py-0.5 bg-white/10 rounded text-xs font-sans text-gray-200" data-id="element-384">
              ⌘K
            </kbd>{' '}
            to navigate
          </p>
        </div>
      </div>
    </div>;
}